import {
  ArrowDownOutlined,
  ArrowUpOutlined,
  BellOutlined,
  CalendarOutlined,
  CheckCircleOutlined,
  CheckOutlined,
  CopyOutlined,
  FileTextOutlined,
  PhoneOutlined,
  ReloadOutlined,
} from '@ant-design/icons';
import {
  Button,
  Card,
  Col,
  Descriptions,
  Divider,
  Empty,
  message,
  Modal,
  Row,
  Space,
  Spin,
  Tag,
  Typography,
} from 'antd';
import { useContext, useEffect, useState } from 'react';
import ChatBubble from '../../components/ChatBubble';
import { Agent } from '../../types/index';
import { UserContext } from '../../UserContext';
import {
  formatDateFromUTCtoAgent,
  getAgentTimezone,
  getCrmLink,
} from './utils';
import { escapeHTML, sanitizeHTML } from '../../utils';

const { Title, Link, Text } = Typography;

interface CallItem {
  id: string;
  transcript: string;
  created_at: string;
  inbound?: boolean;
  retell_call_id?: string;
  external_conversation_id?: string;
  recording_url?: string;
  trace_id?: string;
  twilio_call_id?: string;
  updated_at?: string;
  _created_at?: string;
  client_id?: number;
  prompt_result?: {
    outcome?: string;
    note?: string;
    tasks?: any[];
    appointments?: any[];
    personalization?: {
      sync_time?: string;
      assistant_name?: string;
      agents_nickname?: string;
    };
    header?: string;
    is_no_show?: boolean;
    should_post?: boolean;
    should_post_note?: boolean;
    rescheduled_date_time?: string;
    should_post_justification?: string;
    should_post_note_justification?: string;
  };
  agent_calls?: {
    lead?: {
      source_id?: string;
      id?: string;
      first_name?: string;
      last_name?: string;
      email?: string;
      phone?: string;
      stage?: string;
      deal_stage?: string;
      summary?: string;
      client_id?: number;
      deal_price?: number;
      source_updated_at?: string;
      relationships?: Array<{
        type?: string;
        email?: string;
        phone?: string;
        last_name?: string;
        first_name?: string;
      }>;
      deal_close_date?: string;
      last_activity?: string;
      collaborator_source_ids?: string[];
      deleted_at?: string;
      source?: string;
      is_in_pond?: boolean;
      clean_phone?: string;
      agent_id?: string;
      created_at?: string;
      formatted_stage?: string;
      lead_type?: string;
      tags?: string[];
      _created_at?: string;
      zip_code?: string;
      updated_at?: string;
    };
    prompt_result?: {
      note?: string;
      outcome?: string;
      is_no_show?: boolean;
      tasks?: any[];
      rescheduled_date_time?: string;
      should_post_note_justification?: string;
      header?: string;
      should_post?: boolean;
      appointments?: any[];
      should_post_note?: boolean;
      should_post_justification?: string;
    };
    note_id?: string;
    intent?: string;
  }[];
}

const CallList = ({
  agent,
  apiUrl,
  clientConfig,
}: {
  agent: Agent;
  apiUrl: string;
  clientConfig: any;
}) => {
  const user = useContext(UserContext);
  const [apiResponse, setApiResponse] = useState<
    { user_call: CallItem; agent_calls: { agent_call: any }[] }[]
  >([]);
  const [selectedCall, setSelectedCall] = useState<CallItem | null>(null);
  const agentTimezone = getAgentTimezone(agent, clientConfig);
  const [loading, setLoading] = useState(false);
  const [audioSrc, setAudioSrc] = useState<string | null>(null);

  useEffect(() => {
    if (!selectedCall?.id || !user?.accessToken) {
      setAudioSrc(null);
      return;
    }

    let active = true;
    let objectUrl: string | null = null;

    const fetchAudio = async () => {
      try {
        const response = await fetch(
          `${apiUrl}/api/calls/${selectedCall.id}/audio`,
          {
            headers: {
              Authorization: `Bearer ${user.accessToken}`,
            },
          },
        );
        if (response.ok && active) {
          const blob = await response.blob();
          objectUrl = URL.createObjectURL(blob);
          setAudioSrc(objectUrl);
        }
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error('Error fetching audio:', err);
      }
    };

    void fetchAudio();

    return () => {
      active = false;
      if (objectUrl) {
        URL.revokeObjectURL(objectUrl);
      }
      setAudioSrc(null);
    };
  }, [selectedCall?.id, apiUrl, user?.accessToken]);

  const fetchCalls = async () => {
    try {
      setLoading(true);
      if (!user?.accessToken) {
        setLoading(false);
        return;
      }

      const url = `${apiUrl}/api/calls/agent/${agent.id}`;
      const response = await fetch(url, {
        headers: {
          Authorization: `Bearer ${user.accessToken}`,
        },
      });

      const jsonData = await response.json();
      setApiResponse(jsonData);
      setLoading(false);
    } catch (err) {
      console.error('An error occurred while fetching the calls:', err);
    }
  };

  useEffect(() => {
    fetchCalls();
  }, [agent.id]);

  const openModal = (
    callItem: CallItem,
    rawAgentCalls: { agent_call: any }[],
  ) => {
    const enrichedCall: CallItem = {
      ...callItem,
      agent_calls: (rawAgentCalls || [])
        .filter(({ agent_call: ac }) => !!ac)
        .map(({ agent_call: ac }) => ({
          lead: ac.lead_info || ac.lead || null,
          prompt_result: ac.prompt_result,
          note_id: ac.note_id,
          intent: ac.intent,
        })),
    };
    setSelectedCall(enrichedCall);
  };

  const closeModal = () => setSelectedCall(null);

  const [copied, setCopied] = useState(false);
  // Add sortAsc state
  const [sortAsc, setSortAsc] = useState(false);

  const copyToClipboard = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      message.success('Transcript copied to clipboard!');
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      message.error('Failed to copy transcript');
    }
  };

  // Find the most recent (by created_at) valid personalization
  let defaultPersonalization: any = null;
  let latestPersonalizationTime: string | null = null;
  if (apiResponse && apiResponse.length > 0) {
    apiResponse.forEach(resp => {
      const p = resp.user_call?.prompt_result?.personalization;
      if (p && (p.assistant_name || p.agents_nickname)) {
        const t = resp.user_call.created_at;
        if (
          !latestPersonalizationTime ||
          new Date(t) > new Date(latestPersonalizationTime)
        ) {
          defaultPersonalization = p;
          latestPersonalizationTime = t;
        }
      }
    });
  }

  // Shared date tag component for displaying natural language and ISO date in agent timezone
  interface DateTagProps {
    date_string?: string | null;
    date?: string | null;
    agentTimezone: string;
    color?: string;
    icon?: React.ReactNode;
  }
  const DateTag: React.FC<DateTagProps> = ({
    date_string,
    date,
    agentTimezone,
    color = 'blue',
    icon,
  }) => {
    if (!date_string && !date) {
      return null;
    }
    return (
      <Tag color={color} icon={icon}>
        {date_string}
        {date && (
          <span style={{ marginLeft: 6, fontWeight: 400 }}>
            ( {formatDateFromUTCtoAgent(date, agentTimezone)} )
          </span>
        )}
        {!date_string && date && formatDateFromUTCtoAgent(date, agentTimezone)}
      </Tag>
    );
  };

  // Shared lead tag with CRM link
  interface LeadTagProps {
    lead: any;
    clientConfig: any;
  }
  const LeadTag: React.FC<LeadTagProps> = ({ lead, clientConfig }) => {
    if (!lead) {
      return null;
    }
    // Always pass an object for clientConfig, and a string for source_id
    const crmConfigObj =
      typeof clientConfig === 'object' && clientConfig !== null
        ? clientConfig
        : {};
    const crmSourceId = String(lead.source_id || '');
    const crmLink = getCrmLink(crmConfigObj, crmSourceId) || '';
    return (
      <span>
        <Tag
          color="blue"
          icon={
            <span aria-label="lead" role="img">
              👤
            </span>
          }
        >
          {lead.first_name} {lead.last_name}
        </Tag>
        {crmSourceId && crmLink && (
          <Link
            href={crmLink}
            rel="noopener noreferrer"
            style={{ marginLeft: 8 }}
            target="_blank"
          >
            View in CRM
          </Link>
        )}
      </span>
    );
  };

  // Shared tag list
  const TagList: React.FC<{ tags: string[] }> = ({ tags }) => (
    <Space wrap>
      {tags.map((tag, idx) => (
        <Tag color="blue" key={idx}>
          {tag}
        </Tag>
      ))}
    </Space>
  );

  // Shared relationship list
  const RelationshipList: React.FC<{ relationships: any[] }> = ({
    relationships,
  }) => (
    <Space direction="vertical" size="small" style={{ width: '100%' }}>
      {relationships.map((relationship, idx) => (
        <Row align="middle" justify="space-between" key={idx}>
          <Col>
            <Text>{relationship.type || 'Unknown Type'}</Text>
          </Col>
          <Col>
            <Text>
              {relationship.first_name} {relationship.last_name}
              {relationship.email && ` • ${relationship.email}`}
              {relationship.phone && ` • ${relationship.phone}`}
            </Text>
          </Col>
        </Row>
      ))}
    </Space>
  );

  // Shared info descriptions
  interface InfoDescriptionsProps {
    items: { label: string; value: React.ReactNode }[];
    column?: number;
    size?: 'small' | 'default';
    style?: React.CSSProperties;
  }
  const InfoDescriptions: React.FC<InfoDescriptionsProps> = ({
    items,
    column = 1,
    size = 'small',
    style,
  }) => (
    <Descriptions bordered column={column} size={size} style={style}>
      {items.map((item, idx) => (
        <Descriptions.Item key={idx} label={item.label}>
          {item.value}
        </Descriptions.Item>
      ))}
    </Descriptions>
  );

  // Shared item row for tasks, reminders, appointments
  interface ItemRowProps {
    main: React.ReactNode;
    body?: React.ReactNode;
    dateTag?: React.ReactNode;
    leadTag?: React.ReactNode;
    style?: React.CSSProperties;
  }
  const ItemRow: React.FC<ItemRowProps> = ({
    main,
    body,
    dateTag,
    leadTag,
    style,
  }) => (
    <Row align="middle" justify="space-between" style={style}>
      <Col flex="auto">
        <Text strong>{main}</Text>
        {body && (
          <div style={{ marginTop: 4 }}>
            <Text style={{ fontSize: 12 }} type="secondary">
              {body}
            </Text>
          </div>
        )}
      </Col>
      {dateTag && <Col>{dateTag}</Col>}
      {leadTag && <Col>{leadTag}</Col>}
    </Row>
  );

  // Speaker Key/Legend as a component to avoid scoping issues
  const LegendKey: React.FC = () => {
    // Compute personalization for legend
    let personalization = null;
    let assistantName = 'Agent';
    let agentNickname = 'User';
    if (
      apiResponse &&
      apiResponse.length > 0 &&
      apiResponse[apiResponse.length - 1]?.user_call?.prompt_result
        ?.personalization
    ) {
      personalization =
        apiResponse[apiResponse.length - 1]?.user_call?.prompt_result
          ?.personalization;
    } else if (defaultPersonalization) {
      personalization = defaultPersonalization;
    }
    if (personalization) {
      assistantName = personalization.assistant_name || 'Alex';
      agentNickname = personalization.agents_nickname || agent.first_name;
    } else {
      assistantName = 'Alex';
      agentNickname = agent.first_name;
    }
    // Always use green for Agent/User
    return (
      <div style={{ fontSize: 14 }}>
        <Text style={{ fontSize: 13 }} type="secondary">
          Speaker Key:{' '}
        </Text>
        <span
          style={{
            background: '#4c1d95',
            color: '#fff',
            fontWeight: 600,
            borderRadius: '3px',
            padding: '0 4px',
            fontSize: 12,
          }}
        >
          Assistant
        </span>
        <span
          style={{
            background: '#059669', // Always green
            color: '#fff',
            fontWeight: 600,
            borderRadius: '3px',
            padding: '0 4px',
            fontSize: 12,
            marginLeft: 6,
          }}
        >
          Agent/User
        </span>
      </div>
    );
  };

  // Utility to color and replace speaker labels in transcript
  function colorizeTranscript(
    transcript: string,
    assistantName: string,
    agentNickname: string,
  ): string {
    const assistantColor =
      'background: #4c1d95; color: #fff; font-weight: 600; border-radius: 3px; padding: 0 4px;'; // Darker Purple for Assistant
    const agentColor =
      'background: #059669; color: #fff; font-weight: 600; border-radius: 3px; padding: 0 4px;'; // Always green for Agent/User

    // Escape the entire transcript first
    const escapedTranscript = escapeHTML(transcript);

    // Build normalized label sets for matching
    const escapeRegex = (s: string) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const assistantLabels = ['Agent', assistantName].filter(Boolean);
    const userLabels = ['User', agentNickname].filter(Boolean);

    const allLabels = [
      ...assistantLabels.map(escapeRegex),
      ...userLabels.map(escapeRegex),
    ];

    if (allLabels.length === 0) {
      return escapedTranscript;
    }

    // Match at start of string or after newline, followed by any known label and a colon
    const pattern = new RegExp(`(^|\\n)(${allLabels.join('|')}):`, 'gi');

    return escapedTranscript.replace(pattern, (match, p1, p2) => {
      // Decide if the matched label belongs to assistant or user (case-insensitive)
      const isAssistant = assistantLabels.some(
        l => l.toLowerCase() === String(p2).toLowerCase(),
      );
      const labelSpan = isAssistant
        ? `<span style=\"${assistantColor}\">${escapeHTML(assistantName)}:</span>`
        : `<span style=\"${agentColor}\">${escapeHTML(agentNickname)}:</span>`;
      return `${p1}${labelSpan}`;
    });
  }

  return (
    <Card bordered>
      <Row justify="end" style={{ marginBottom: 12 }}>
        <Col>
          <Button
            icon={<ReloadOutlined />}
            loading={loading}
            onClick={fetchCalls}
            type="default"
          >
            Refresh
          </Button>
        </Col>
        <Col>
          <Button
            icon={sortAsc ? <ArrowDownOutlined /> : <ArrowUpOutlined />}
            onClick={() => setSortAsc(prev => !prev)}
            style={{ marginLeft: 8 }}
            type="default"
          >
            Sort {sortAsc ? 'Descending' : 'Ascending'}
          </Button>
        </Col>
      </Row>
      <Spin spinning={loading}>
        <Space direction="vertical" size="middle" style={{ width: '100%' }}>
          {[...apiResponse]
            .sort((a, b) =>
              sortAsc
                ? new Date(a.user_call.created_at).getTime() -
                  new Date(b.user_call.created_at).getTime()
                : new Date(b.user_call.created_at).getTime() -
                  new Date(a.user_call.created_at).getTime(),
            )
            .map((response, index) => {
              const isOutbound = !response.user_call.inbound;
              let personalization =
                response.user_call.prompt_result?.personalization;
              // Only use if valid
              if (
                !personalization ||
                (!personalization.assistant_name &&
                  !personalization.agents_nickname)
              ) {
                personalization = defaultPersonalization;
              }
              const transcript = response.user_call.transcript;
              let assistantName = 'Agent';
              let agentNickname = 'User';
              if (personalization) {
                assistantName = personalization.assistant_name || 'Alex';
                agentNickname =
                  personalization.agents_nickname || agent.first_name;
              } else {
                assistantName = 'Alex';
                agentNickname = agent.first_name;
              }
              const coloredTranscript = colorizeTranscript(
                transcript,
                assistantName,
                agentNickname,
              );
              // Collect notes for inline display
              const inlineNotes: { leadName: string | null; note: string }[] =
                [];
              const topNote = response.user_call.prompt_result?.note;
              if (topNote?.trim()) {
                inlineNotes.push({ leadName: null, note: topNote.trim() });
              }
              const seenNotes = new Set<string>();
              if (topNote?.trim()) {
                seenNotes.add(`|${topNote.trim()}`);
              }
              (response.agent_calls || []).forEach(({ agent_call: ac }) => {
                if (!ac) {
                  return;
                }
                const acNote = ac.prompt_result?.note;
                if (acNote?.trim()) {
                  const leadInfo = ac.lead_info || ac.lead;
                  const leadName = leadInfo
                    ? `${leadInfo.first_name || ''} ${leadInfo.last_name || ''}`.trim() ||
                      null
                    : null;
                  const key = `${leadName ?? ''}|${acNote.trim()}`;
                  if (!seenNotes.has(key)) {
                    seenNotes.add(key);
                    inlineNotes.push({ leadName, note: acNote.trim() });
                  }
                }
              });

              // Collect tasks for inline display
              const inlineTasks: { leadName: string | null; title: string }[] =
                [];
              const seenTasks = new Set<string>();
              (response.user_call.prompt_result?.tasks || []).forEach(
                (task: any) => {
                  const title =
                    task.title || task.short_description || task.description;
                  const key = `|${title}|${task.date ?? ''}`;
                  if (title && !seenTasks.has(key)) {
                    seenTasks.add(key);
                    inlineTasks.push({ leadName: null, title });
                  }
                },
              );
              (response.agent_calls || []).forEach(({ agent_call: ac }) => {
                if (!ac) {
                  return;
                }
                const leadInfo = ac.lead_info || ac.lead;
                const leadName = leadInfo
                  ? `${leadInfo.first_name || ''} ${leadInfo.last_name || ''}`.trim() ||
                    null
                  : null;
                (ac.prompt_result?.tasks || []).forEach((task: any) => {
                  const title =
                    task.title || task.short_description || task.description;
                  const key = `${leadName ?? ''}|${title}|${task.date ?? ''}`;
                  if (title && !seenTasks.has(key)) {
                    seenTasks.add(key);
                    inlineTasks.push({ leadName, title });
                  }
                });
              });

              return (
                <Row justify="start" key={response.user_call.id || index}>
                  <Col>
                    <div
                      onClick={() =>
                        openModal(
                          response.user_call,
                          response.agent_calls || [],
                        )
                      }
                      onKeyPress={e => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          openModal(
                            response.user_call,
                            response.agent_calls || [],
                          );
                        }
                      }}
                      role="button"
                      style={{
                        cursor: 'pointer',
                        outline: 'none',
                        textAlign: 'left',
                      }}
                      tabIndex={0}
                    >
                      <div style={{ marginBottom: 4 }}>
                        <Tag
                          color={isOutbound ? 'blue' : 'green'}
                          icon={<PhoneOutlined />}
                        >
                          {isOutbound ? 'Outbound' : 'Inbound'}
                        </Tag>
                      </div>
                      <ChatBubble
                        isOwnMessage={false}
                        message={coloredTranscript}
                        timestamp={formatDateFromUTCtoAgent(
                          response.user_call.created_at,
                          agentTimezone,
                        )}
                      />
                      {(inlineNotes.length > 0 || inlineTasks.length > 0) && (
                        <div
                          onClick={e => e.stopPropagation()}
                          style={{
                            marginTop: 6,
                            padding: '6px 10px',
                            background: '#fafafa',
                            border: '1px solid #f0f0f0',
                            borderRadius: 4,
                            fontSize: 12,
                          }}
                        >
                          {inlineNotes.length > 0 && (
                            <div
                              style={{
                                marginBottom: inlineTasks.length > 0 ? 6 : 0,
                              }}
                            >
                              <Text style={{ fontSize: 12, fontWeight: 600 }}>
                                <FileTextOutlined /> Note
                                {inlineNotes.length > 1 ? 's' : ''}:
                              </Text>
                              {inlineNotes.map((n, i) => (
                                <div
                                  key={i}
                                  style={{ marginTop: 2, color: '#555' }}
                                >
                                  {n.leadName && (
                                    <Tag color="blue" style={{ fontSize: 11 }}>
                                      {n.leadName}
                                    </Tag>
                                  )}
                                  {n.note}
                                </div>
                              ))}
                            </div>
                          )}
                          {inlineTasks.length > 0 && (
                            <div>
                              <Text style={{ fontSize: 12, fontWeight: 600 }}>
                                <CheckCircleOutlined /> Task
                                {inlineTasks.length > 1 ? 's' : ''}:
                              </Text>
                              {inlineTasks.map((t, i) => (
                                <div
                                  key={i}
                                  style={{ marginTop: 2, color: '#555' }}
                                >
                                  {t.leadName && (
                                    <Tag color="blue" style={{ fontSize: 11 }}>
                                      {t.leadName}
                                    </Tag>
                                  )}
                                  {t.title}
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </Col>
                </Row>
              );
            })}
        </Space>

        {/* Speaker Key/Legend - Bottom Right */}
        <Row justify="end" style={{ marginTop: 16 }}>
          <Col>
            <LegendKey />
          </Col>
        </Row>
      </Spin>

      <Modal
        footer={null}
        onCancel={closeModal}
        open={!!selectedCall}
        title={
          selectedCall ? (
            <Space>
              <span aria-label="call" role="img">
                📞
              </span>{' '}
              Call Details
            </Space>
          ) : null
        }
        width={950}
      >
        {selectedCall && (
          <Space direction="vertical" size="large" style={{ width: '100%' }}>
            <Card bordered>
              <Row gutter={[16, 16]}>
                <Col span={12}>
                  <Title level={5}>Call Information</Title>
                  <InfoDescriptions
                    items={[
                      { label: 'Call ID', value: selectedCall.id },
                      {
                        label: 'Type',
                        value: (
                          <Tag color={selectedCall.inbound ? 'green' : 'blue'}>
                            {selectedCall.inbound ? 'Inbound' : 'Outbound'}
                          </Tag>
                        ),
                      },
                      {
                        label: 'Timestamp',
                        value: formatDateFromUTCtoAgent(
                          selectedCall.created_at,
                          agentTimezone,
                        ),
                      },
                      {
                        label: 'Retell Call ID',
                        value: selectedCall.retell_call_id ? (
                          <Link
                            href={`${process.env.REACT_APP_REALLAI_CALL_HISTORY_URL || ''}${selectedCall.retell_call_id}`}
                            rel="noopener noreferrer"
                            target="_blank"
                          >
                            {selectedCall.retell_call_id}
                          </Link>
                        ) : (
                          'N/A'
                        ),
                      },
                      {
                        label: 'ElevenLabs Conversation',
                        value: selectedCall.external_conversation_id ? (
                          <Link
                            href={`https://elevenlabs.io/app/agents/history/${selectedCall.external_conversation_id}`}
                            rel="noopener noreferrer"
                            target="_blank"
                          >
                            {selectedCall.external_conversation_id}
                          </Link>
                        ) : (
                          'N/A'
                        ),
                      },
                      {
                        label: 'Recording',
                        value:
                          selectedCall.recording_url ||
                          selectedCall.external_conversation_id ? (
                            audioSrc ? (
                              <audio controls src={audioSrc} />
                            ) : (
                              <Spin size="small" />
                            )
                          ) : (
                            'N/A'
                          ),
                      },
                    ]}
                  />
                </Col>
                <Col span={12}>
                  <Title level={5}>System Information</Title>
                  <InfoDescriptions
                    items={[
                      {
                        label: 'Twilio Call ID',
                        value: selectedCall.twilio_call_id ?? 'N/A',
                      },
                      {
                        label: 'Client ID',
                        value: selectedCall.client_id ?? 'N/A',
                      },
                      {
                        label: 'Updated',
                        value: selectedCall.updated_at
                          ? formatDateFromUTCtoAgent(
                              selectedCall.updated_at,
                              agentTimezone,
                            )
                          : 'N/A',
                      },
                      {
                        label: 'Trace ID',
                        value: selectedCall.trace_id ? (
                          process.env.REACT_APP_PHOENIX_TRACE_URL_PREFIX ? (
                            <Link
                              href={`${process.env.REACT_APP_PHOENIX_TRACE_URL_PREFIX}/${selectedCall.trace_id}`}
                              rel="noopener noreferrer"
                              target="_blank"
                            >
                              {selectedCall.trace_id}
                            </Link>
                          ) : (
                            <Text>{selectedCall.trace_id}</Text>
                          )
                        ) : (
                          'N/A'
                        ),
                      },
                    ]}
                  />
                </Col>
              </Row>
            </Card>
            {(() => {
              let personalization = selectedCall.prompt_result?.personalization;
              if (
                !personalization ||
                (!personalization.assistant_name &&
                  !personalization.agents_nickname &&
                  !personalization.sync_time)
              ) {
                personalization = defaultPersonalization;
              }
              if (
                !personalization ||
                (!personalization.assistant_name &&
                  !personalization.agents_nickname &&
                  !personalization.sync_time)
              ) {
                return null;
              }
              return (
                <Card bordered>
                  <Title level={5}>Personalization</Title>
                  <InfoDescriptions
                    items={[
                      {
                        label: 'Assistant Name',
                        value: personalization.assistant_name ?? 'N/A',
                      },
                      {
                        label: 'Agent Nickname',
                        value: personalization.agents_nickname ?? 'N/A',
                      },
                      {
                        label: 'Sync Time',
                        value: personalization.sync_time
                          ? formatDateFromUTCtoAgent(
                              personalization.sync_time,
                              agentTimezone,
                            )
                          : 'N/A',
                      },
                    ]}
                  />
                </Card>
              );
            })()}

            {/* Call Outcome Section */}
            {(() => {
              const hasTopLevelOutcome =
                selectedCall.prompt_result &&
                (selectedCall.prompt_result.header ||
                  selectedCall.prompt_result.outcome ||
                  selectedCall.prompt_result.is_no_show !== undefined ||
                  selectedCall.prompt_result.should_post !== undefined ||
                  selectedCall.prompt_result.should_post_note !== undefined ||
                  selectedCall.prompt_result.rescheduled_date_time ||
                  selectedCall.prompt_result.should_post_justification ||
                  selectedCall.prompt_result.should_post_note_justification);
              const hasAgentCallOutcomes =
                selectedCall.agent_calls &&
                selectedCall.agent_calls.some(
                  agentCall =>
                    agentCall.prompt_result &&
                    (agentCall.prompt_result.header ||
                      agentCall.prompt_result.outcome ||
                      agentCall.prompt_result.is_no_show !== undefined ||
                      agentCall.prompt_result.should_post !== undefined ||
                      agentCall.prompt_result.should_post_note !== undefined ||
                      agentCall.prompt_result.rescheduled_date_time ||
                      agentCall.prompt_result.should_post_justification ||
                      agentCall.prompt_result.should_post_note_justification),
                );
              if (!hasTopLevelOutcome && !hasAgentCallOutcomes) {
                return null;
              }
              return (
                <Card bordered>
                  <Title level={5}>Call Outcome</Title>
                  {hasTopLevelOutcome && (
                    <InfoDescriptions
                      items={[
                        {
                          label: 'Header',
                          value: selectedCall.prompt_result?.header,
                        },
                        {
                          label: 'Outcome',
                          value: selectedCall.prompt_result?.outcome,
                        },
                        {
                          label: 'No Show',
                          value:
                            selectedCall.prompt_result?.is_no_show !== undefined
                              ? selectedCall.prompt_result.is_no_show
                                ? 'Yes'
                                : 'No'
                              : 'N/A',
                        },
                        {
                          label: 'Should Post',
                          value:
                            selectedCall.prompt_result?.should_post !==
                            undefined
                              ? selectedCall.prompt_result.should_post
                                ? 'Yes'
                                : 'No'
                              : 'N/A',
                        },
                        {
                          label: 'Should Post Note',
                          value:
                            selectedCall.prompt_result?.should_post_note !==
                            undefined
                              ? selectedCall.prompt_result.should_post_note
                                ? 'Yes'
                                : 'No'
                              : 'N/A',
                        },
                        {
                          label: 'Rescheduled Date',
                          value: selectedCall.prompt_result
                            ?.rescheduled_date_time
                            ? formatDateFromUTCtoAgent(
                                selectedCall.prompt_result
                                  .rescheduled_date_time,
                                agentTimezone,
                              )
                            : 'N/A',
                        },
                        {
                          label: 'Post Justification',
                          value:
                            selectedCall.prompt_result
                              ?.should_post_justification,
                        },
                        {
                          label: 'Note Justification',
                          value:
                            selectedCall.prompt_result
                              ?.should_post_note_justification,
                        },
                      ]}
                    />
                  )}
                  {hasAgentCallOutcomes && selectedCall.agent_calls && (
                    <>
                      <Divider orientation="left">Leads</Divider>
                      <Space
                        direction="vertical"
                        size="middle"
                        style={{ width: '100%' }}
                      >
                        {selectedCall.agent_calls.map((agentCall, idx) => {
                          if (!agentCall || !agentCall.prompt_result) {
                            return null;
                          }
                          // Defensive: ensure agentCall.lead is defined before accessing tags/relationships
                          const lead = agentCall.lead;
                          const hasOutcome =
                            agentCall.prompt_result.header ||
                            agentCall.prompt_result.outcome ||
                            agentCall.prompt_result.is_no_show !== undefined ||
                            agentCall.prompt_result.should_post !== undefined ||
                            agentCall.prompt_result.should_post_note !==
                              undefined ||
                            agentCall.prompt_result.rescheduled_date_time ||
                            agentCall.prompt_result.should_post_justification ||
                            agentCall.prompt_result
                              .should_post_note_justification;
                          if (!hasOutcome) {
                            return null;
                          }
                          const leadName =
                            (lead &&
                              `${lead.first_name || ''} ${lead.last_name || ''}`.trim()) ||
                            `Lead ${idx + 1}`;
                          return (
                            <Card bordered key={idx}>
                              <Row align="middle" justify="space-between">
                                <Col>
                                  <Space>
                                    <Tag color="blue">{idx + 1}</Tag>
                                    <Tag
                                      color="blue"
                                      icon={
                                        <span aria-label="lead" role="img">
                                          👤
                                        </span>
                                      }
                                    >
                                      {agentCall.lead?.first_name}{' '}
                                      {agentCall.lead?.last_name}
                                    </Tag>
                                  </Space>
                                </Col>
                                {agentCall.lead && agentCall.lead.source_id && (
                                  <Col>
                                    <Link
                                      href={getCrmLink(
                                        clientConfig,
                                        agentCall.lead.source_id,
                                      )}
                                      rel="noopener noreferrer"
                                      target="_blank"
                                    >
                                      View in CRM
                                    </Link>
                                  </Col>
                                )}
                              </Row>
                              <InfoDescriptions
                                items={[
                                  {
                                    label: 'Email',
                                    value: agentCall.lead?.email || 'N/A',
                                  },
                                  {
                                    label: 'Phone',
                                    value: agentCall.lead?.phone || 'N/A',
                                  },
                                  {
                                    label: 'Agent ID',
                                    value: agentCall.lead?.agent_id || 'N/A',
                                  },
                                  {
                                    label: 'Is In Pond',
                                    value: agentCall.lead?.is_in_pond
                                      ? 'Yes'
                                      : 'No',
                                  },
                                ]}
                              />

                              {/* Tags */}
                              {lead?.tags && lead.tags.length > 0 ? (
                                <div>
                                  <Title level={5}>Tags</Title>
                                  <TagList tags={lead.tags} />
                                </div>
                              ) : null}

                              {/* Relationships */}
                              {lead?.relationships &&
                              lead.relationships.length > 0 ? (
                                <div>
                                  <Title level={5}>Relationships</Title>
                                  <RelationshipList
                                    relationships={lead.relationships}
                                  />
                                </div>
                              ) : null}

                              <div>
                                {/* Agent Call Details */}
                                <InfoDescriptions
                                  items={[
                                    {
                                      label: 'Header',
                                      value:
                                        agentCall.prompt_result?.header ||
                                        'N/A',
                                    },
                                    {
                                      label: 'Outcome',
                                      value:
                                        agentCall.prompt_result?.outcome ||
                                        'N/A',
                                    },
                                    {
                                      label: 'No Show',
                                      value: agentCall.prompt_result?.is_no_show
                                        ? 'Yes'
                                        : 'No',
                                    },
                                    {
                                      label: 'Note ID',
                                      value: agentCall.note_id,
                                    },
                                    {
                                      label: 'Intent',
                                      value: agentCall.intent,
                                    },
                                    {
                                      label: 'Should Post',
                                      value:
                                        agentCall.prompt_result?.should_post !==
                                        undefined
                                          ? agentCall.prompt_result.should_post
                                            ? 'Yes'
                                            : 'No'
                                          : 'N/A',
                                    },
                                    {
                                      label: 'Should Post Note',
                                      value:
                                        agentCall.prompt_result
                                          ?.should_post_note !== undefined
                                          ? agentCall.prompt_result
                                              .should_post_note
                                            ? 'Yes'
                                            : 'No'
                                          : 'N/A',
                                    },
                                    {
                                      label: 'Rescheduled Date',
                                      value: agentCall.prompt_result
                                        ?.rescheduled_date_time
                                        ? formatDateFromUTCtoAgent(
                                            agentCall.prompt_result
                                              .rescheduled_date_time,
                                            agentTimezone,
                                          )
                                        : 'N/A',
                                    },
                                  ]}
                                />

                                {/* Justifications */}
                                {agentCall.prompt_result
                                  ?.should_post_justification && (
                                  <InfoDescriptions
                                    items={[
                                      {
                                        label: 'Post Justification',
                                        value: (
                                          <Text>
                                            {
                                              agentCall.prompt_result
                                                .should_post_justification
                                            }
                                          </Text>
                                        ),
                                      },
                                    ]}
                                  />
                                )}
                                {agentCall.prompt_result
                                  ?.should_post_note_justification && (
                                  <InfoDescriptions
                                    items={[
                                      {
                                        label: 'Note Justification',
                                        value: (
                                          <Text>
                                            {
                                              agentCall.prompt_result
                                                .should_post_note_justification
                                            }
                                          </Text>
                                        ),
                                      },
                                    ]}
                                  />
                                )}
                              </div>
                              {/* Tasks & Appointments */}
                              <Space
                                direction="vertical"
                                size="small"
                                style={{ width: '100%' }}
                              >
                                {/* Notes Card */}
                                <Card
                                  size="small"
                                  style={{ marginBottom: 16 }}
                                  title={
                                    <>
                                      <FileTextOutlined /> Notes
                                    </>
                                  }
                                >
                                  {agentCall.prompt_result?.note &&
                                  agentCall.prompt_result.note.trim() ? (
                                    <Row
                                      style={{
                                        padding: '8px 0',
                                        borderBottom: '1px solid #f0f0f0',
                                      }}
                                    >
                                      <Col flex="auto">
                                        <Text>
                                          {agentCall.prompt_result.note}
                                        </Text>
                                      </Col>
                                    </Row>
                                  ) : (
                                    <Empty
                                      description="No notes"
                                      image={Empty.PRESENTED_IMAGE_SIMPLE}
                                    />
                                  )}
                                </Card>

                                {/* Tasks Card */}
                                <Card
                                  size="small"
                                  style={{ marginBottom: 16 }}
                                  title={
                                    <>
                                      <CheckCircleOutlined /> Tasks
                                    </>
                                  }
                                >
                                  {agentCall.prompt_result?.tasks &&
                                  agentCall.prompt_result.tasks.length > 0 ? (
                                    <Space
                                      direction="vertical"
                                      size="small"
                                      style={{ width: '100%' }}
                                    >
                                      {agentCall.prompt_result.tasks.map(
                                        (task: any, tIdx: number) => {
                                          const main =
                                            task.title ||
                                            task.short_description ||
                                            task.description;
                                          const body =
                                            (task.title ||
                                              task.short_description) &&
                                            task.description &&
                                            task.description !== task.title &&
                                            task.description !==
                                              task.short_description
                                              ? task.description
                                              : null;
                                          return (
                                            <ItemRow
                                              body={body}
                                              dateTag={
                                                task.date_string ? (
                                                  <DateTag
                                                    agentTimezone={
                                                      agentTimezone
                                                    }
                                                    color="blue"
                                                    date={task.date}
                                                    date_string={
                                                      task.date_string
                                                    }
                                                    icon={<CalendarOutlined />}
                                                  />
                                                ) : null
                                              }
                                              leadTag={
                                                task.lead ? (
                                                  <LeadTag
                                                    clientConfig={
                                                      clientConfig || {}
                                                    }
                                                    lead={
                                                      task.lead && {
                                                        ...task.lead,
                                                        source_id: String(
                                                          task.lead.source_id ??
                                                            '',
                                                        ),
                                                      }
                                                    }
                                                  />
                                                ) : null
                                              }
                                              main={main}
                                              style={{
                                                padding: '8px 0',
                                                borderBottom:
                                                  '1px solid #f0f0f0',
                                              }}
                                            />
                                          );
                                        },
                                      )}
                                    </Space>
                                  ) : (
                                    <Empty
                                      description="No tasks"
                                      image={Empty.PRESENTED_IMAGE_SIMPLE}
                                    />
                                  )}
                                </Card>

                                {/* Reminders Card */}
                                <Card
                                  size="small"
                                  style={{ marginBottom: 16 }}
                                  title={
                                    <>
                                      <BellOutlined /> Reminders
                                    </>
                                  }
                                >
                                  {agentCall.prompt_result?.tasks &&
                                  agentCall.prompt_result.tasks.some(
                                    (task: any) => task.type === 'reminder',
                                  ) ? (
                                    <Space
                                      direction="vertical"
                                      size="small"
                                      style={{ width: '100%' }}
                                    >
                                      {agentCall.prompt_result.tasks
                                        .filter(
                                          (task: any) =>
                                            task.type === 'reminder',
                                        )
                                        .map((task: any, tIdx: number) => {
                                          const main =
                                            task.title ||
                                            task.short_description ||
                                            task.description;
                                          const body =
                                            (task.title ||
                                              task.short_description) &&
                                            task.description &&
                                            task.description !== task.title &&
                                            task.description !==
                                              task.short_description
                                              ? task.description
                                              : null;
                                          return (
                                            <ItemRow
                                              body={body}
                                              dateTag={
                                                task.date_string ? (
                                                  <DateTag
                                                    agentTimezone={
                                                      agentTimezone
                                                    }
                                                    color="orange"
                                                    date={task.date}
                                                    date_string={
                                                      task.date_string
                                                    }
                                                    icon={<BellOutlined />}
                                                  />
                                                ) : null
                                              }
                                              leadTag={
                                                task.lead ? (
                                                  <LeadTag
                                                    clientConfig={
                                                      clientConfig || {}
                                                    }
                                                    lead={
                                                      task.lead && {
                                                        ...task.lead,
                                                        source_id: String(
                                                          task.lead.source_id ??
                                                            '',
                                                        ),
                                                      }
                                                    }
                                                  />
                                                ) : null
                                              }
                                              main={main}
                                              style={{
                                                padding: '8px 0',
                                                borderBottom:
                                                  '1px solid #f0f0f0',
                                              }}
                                            />
                                          );
                                        })}
                                    </Space>
                                  ) : (
                                    <Empty
                                      description="No reminders"
                                      image={Empty.PRESENTED_IMAGE_SIMPLE}
                                    />
                                  )}
                                </Card>

                                {/* Appointments Card */}
                                <Card
                                  size="small"
                                  title={
                                    <>
                                      <CalendarOutlined /> Appointments
                                    </>
                                  }
                                >
                                  {agentCall.prompt_result?.appointments &&
                                  agentCall.prompt_result.appointments.length >
                                    0 ? (
                                    <Space
                                      direction="vertical"
                                      size="small"
                                      style={{ width: '100%' }}
                                    >
                                      {agentCall.prompt_result.appointments.map(
                                        (appt: any, aIdx: number) => {
                                          const main =
                                            appt.title ||
                                            appt.short_description ||
                                            appt.description;
                                          const body =
                                            (appt.title ||
                                              appt.short_description) &&
                                            appt.description &&
                                            appt.description !== appt.title &&
                                            appt.description !==
                                              appt.short_description
                                              ? appt.description
                                              : null;
                                          return (
                                            <ItemRow
                                              body={body}
                                              dateTag={
                                                appt.date_string ? (
                                                  <DateTag
                                                    agentTimezone={
                                                      agentTimezone
                                                    }
                                                    color="green"
                                                    date={appt.date}
                                                    date_string={
                                                      appt.date_string
                                                    }
                                                    icon={<CalendarOutlined />}
                                                  />
                                                ) : null
                                              }
                                              leadTag={
                                                appt.lead ? (
                                                  <LeadTag
                                                    clientConfig={
                                                      clientConfig || {}
                                                    }
                                                    lead={
                                                      appt.lead && {
                                                        ...appt.lead,
                                                        source_id: String(
                                                          appt.lead.source_id ??
                                                            '',
                                                        ),
                                                      }
                                                    }
                                                  />
                                                ) : null
                                              }
                                              main={main}
                                              style={{
                                                padding: '8px 0',
                                                borderBottom:
                                                  '1px solid #f0f0f0',
                                              }}
                                            />
                                          );
                                        },
                                      )}
                                    </Space>
                                  ) : (
                                    <Empty
                                      description="No appointments"
                                      image={Empty.PRESENTED_IMAGE_SIMPLE}
                                    />
                                  )}
                                </Card>
                              </Space>
                            </Card>
                          );
                        })}
                      </Space>
                    </>
                  )}
                </Card>
              );
            })()}

            {/* Tasks Section */}
            {(() => {
              // Helper function to check if lead name appears in text (with flexible matching)
              const nameMatchesText = (
                leadName: string,
                text: string,
              ): boolean => {
                if (!leadName || !text) {
                  return false;
                }
                const normalizedText = text.toLowerCase().replace(/\s+/g, '');
                const normalizedLeadName = leadName
                  .toLowerCase()
                  .replace(/\s+/g, '');
                if (normalizedText.includes(normalizedLeadName)) {
                  return true;
                }
                const nameParts = leadName
                  .toLowerCase()
                  .split(/\s+/)
                  .filter(part => part.length > 0);
                for (const part of nameParts) {
                  if (part.length > 2 && normalizedText.includes(part)) {
                    return true;
                  }
                }
                return false;
              };
              // Helper function to determine if an item is a reminder
              const isReminder = (task: any): boolean => {
                const text =
                  `${task.description || ''} ${task.short_description || ''} ${task.date_string || ''}`.toLowerCase();
                const timePatterns = [
                  /\bat \d{1,2}(?::\d{2})?\s*(am|pm)\b/i,
                  /\b\d{1,2}(?::\d{2})?\s*(am|pm)\b/i,
                  /\btoday\b/,
                  /\btomorrow\b/,
                  /\bremind\b/,
                  /\breminder\b/,
                ];
                return timePatterns.some(pattern => pattern.test(text));
              };
              // Gather all tasks from top-level and agent_calls
              const agentCalls = selectedCall.agent_calls || [];
              // Collect all tasks
              const topTasks = (selectedCall.prompt_result?.tasks || []).map(
                task => ({ ...task, lead: null }),
              );
              const agentTasks = agentCalls.flatMap(agentCall => {
                return (agentCall.prompt_result?.tasks || []).map(
                  (task: any) => ({ ...task, lead: agentCall.lead || null }),
                );
              });
              // Deduplicate by description+date
              const allTasks = [...topTasks, ...agentTasks];
              const seen = new Set();
              const dedupedTasks = allTasks.filter(task => {
                const key = `${task.description || task.short_description || ''}|${task.date || ''}`;
                if (seen.has(key)) {
                  return false;
                }
                seen.add(key);
                return true;
              });
              // Filter out tasks that have a lead name in their description
              const topLevelTasks = dedupedTasks.filter(task => {
                const taskText = `${task.description || ''} ${task.short_description || ''}`;
                for (const agentCall of agentCalls) {
                  const leadName =
                    `${agentCall.lead?.first_name || ''} ${agentCall.lead?.last_name || ''}`.trim();
                  if (nameMatchesText(leadName, taskText)) {
                    return false;
                  }
                }
                return true;
              });
              // Separate tasks and reminders
              const topLevelReminders = topLevelTasks.filter(task =>
                isReminder(task),
              );
              const topLevelGeneralTasks = topLevelTasks.filter(
                task => !isReminder(task),
              );
              if (
                topLevelGeneralTasks.length === 0 &&
                topLevelReminders.length === 0
              ) {
                return null;
              }
              return (
                <Card bordered>
                  <Title level={5}>Tasks</Title>
                  {topLevelGeneralTasks.length > 0 && (
                    <>
                      <Title level={5}>General Tasks</Title>
                      <Space
                        direction="vertical"
                        size="small"
                        style={{ width: '100%' }}
                      >
                        {topLevelGeneralTasks.map((task, idx) => {
                          const main =
                            task.title ||
                            task.short_description ||
                            task.description;
                          const body =
                            (task.title || task.short_description) &&
                            task.description &&
                            task.description !== task.title &&
                            task.description !== task.short_description
                              ? task.description
                              : null;
                          return (
                            <ItemRow
                              body={body}
                              dateTag={
                                task.date_string ? (
                                  <DateTag
                                    agentTimezone={agentTimezone}
                                    color="blue"
                                    date={task.date}
                                    date_string={task.date_string}
                                    icon={<CalendarOutlined />}
                                  />
                                ) : null
                              }
                              leadTag={
                                task.lead ? (
                                  <LeadTag
                                    clientConfig={clientConfig || {}}
                                    lead={
                                      task.lead && {
                                        ...task.lead,
                                        source_id: String(
                                          task.lead.source_id ?? '',
                                        ),
                                      }
                                    }
                                  />
                                ) : null
                              }
                              main={main}
                              style={{
                                padding: '8px 0',
                                borderBottom: '1px solid #f0f0f0',
                              }}
                            />
                          );
                        })}
                      </Space>
                    </>
                  )}
                  {topLevelReminders.length > 0 && (
                    <>
                      <Title level={5}>Reminders</Title>
                      <Space
                        direction="vertical"
                        size="small"
                        style={{ width: '100%' }}
                      >
                        {topLevelReminders.map((task, idx) => {
                          const main =
                            task.title ||
                            task.short_description ||
                            task.description;
                          const body =
                            (task.title || task.short_description) &&
                            task.description &&
                            task.description !== task.title &&
                            task.description !== task.short_description
                              ? task.description
                              : null;
                          return (
                            <ItemRow
                              body={body}
                              dateTag={
                                task.date_string ? (
                                  <DateTag
                                    agentTimezone={agentTimezone}
                                    color="blue"
                                    date={task.date}
                                    date_string={task.date_string}
                                    icon={<CalendarOutlined />}
                                  />
                                ) : null
                              }
                              leadTag={
                                task.lead ? (
                                  <LeadTag
                                    clientConfig={clientConfig || {}}
                                    lead={
                                      task.lead && {
                                        ...task.lead,
                                        source_id: String(
                                          task.lead.source_id ?? '',
                                        ),
                                      }
                                    }
                                  />
                                ) : null
                              }
                              main={main}
                              style={{
                                padding: '8px 0',
                                borderBottom: '1px solid #f0f0f0',
                              }}
                            />
                          );
                        })}
                      </Space>
                    </>
                  )}
                </Card>
              );
            })()}

            {/* Appointments Section (top-level, card style, only for appointments not associated with a lead) */}
            {(() => {
              // Helper function to check if lead name appears in text (with flexible matching)
              const nameMatchesText = (
                leadName: string,
                text: string,
              ): boolean => {
                if (!leadName || !text) {
                  return false;
                }
                const normalizedText = text.toLowerCase().replace(/\s+/g, '');
                const normalizedLeadName = leadName
                  .toLowerCase()
                  .replace(/\s+/g, '');

                // Exact match
                if (normalizedText.includes(normalizedLeadName)) {
                  return true;
                }

                // Split lead name and check for partial matches
                const nameParts = leadName
                  .toLowerCase()
                  .split(/\s+/)
                  .filter(part => part.length > 0);
                for (const part of nameParts) {
                  if (part.length > 2 && normalizedText.includes(part)) {
                    return true;
                  }
                }

                return false;
              };

              // Gather all appointments from top-level and agent_calls
              const agentCalls = selectedCall.agent_calls || [];
              // Collect all appointments
              const topAppointments = (
                selectedCall.prompt_result?.appointments || []
              ).map(appt => ({
                ...appt,
                lead: null,
              }));
              const agentAppointments = agentCalls.flatMap(agentCall => {
                if (
                  agentCall.prompt_result &&
                  'appointments' in agentCall.prompt_result &&
                  Array.isArray((agentCall.prompt_result as any).appointments)
                ) {
                  return (agentCall.prompt_result as any).appointments.map(
                    (appt: any) => ({
                      ...appt,
                      lead: agentCall.lead || null,
                    }),
                  );
                }
                return [];
              });
              // Deduplicate by description+date
              const allAppointments = [
                ...topAppointments,
                ...agentAppointments,
              ];
              const seen = new Set();
              const dedupedAppointments = allAppointments.filter(appt => {
                const key = `${appt.description || appt.title || ''}|${appt.date || ''}`;
                if (seen.has(key)) {
                  return false;
                }
                seen.add(key);
                return true;
              });
              // Filter out appointments that have a lead name in their title/description
              const topLevelAppointments = dedupedAppointments.filter(appt => {
                const apptText = `${appt.title || ''} ${appt.description || ''}`;
                // Check if any lead's name appears in the appointment text
                for (const agentCall of agentCalls) {
                  const leadName =
                    `${agentCall.lead?.first_name || ''} ${agentCall.lead?.last_name || ''}`.trim();
                  if (nameMatchesText(leadName, apptText)) {
                    return false; // This appointment matches a lead, so don't show it top-level
                  }
                }
                return true; // No lead name found, show it top-level
              });
              if (topLevelAppointments.length === 0) {
                return null;
              }
              return (
                <Card bordered>
                  <Title level={5}>Appointments</Title>
                  <Space
                    direction="vertical"
                    size="small"
                    style={{ width: '100%' }}
                  >
                    {topLevelAppointments.map((appt, idx) => {
                      const main =
                        appt.title ||
                        appt.short_description ||
                        appt.description;
                      const body =
                        (appt.title || appt.short_description) &&
                        appt.description &&
                        appt.description !== appt.title &&
                        appt.description !== appt.short_description
                          ? appt.description
                          : null;
                      return (
                        <ItemRow
                          body={body}
                          dateTag={
                            appt.date_string ? (
                              <DateTag
                                agentTimezone={agentTimezone}
                                color="blue"
                                date={appt.date}
                                date_string={appt.date_string}
                                icon={<CalendarOutlined />}
                              />
                            ) : null
                          }
                          leadTag={
                            appt.lead ? (
                              <LeadTag
                                clientConfig={clientConfig || {}}
                                lead={
                                  appt.lead && {
                                    ...appt.lead,
                                    source_id: String(
                                      appt.lead.source_id ?? '',
                                    ),
                                  }
                                }
                              />
                            ) : null
                          }
                          main={main}
                          style={{
                            padding: '8px 0',
                            borderBottom: '1px solid #f0f0f0',
                          }}
                        />
                      );
                    })}
                  </Space>
                </Card>
              );
            })()}

            {/* Transcript Section - Moved to bottom */}
            {selectedCall.transcript && (
              <Card bordered>
                <Row align="middle" justify="space-between">
                  <Col>
                    <Title level={5}>Transcript</Title>
                  </Col>
                  <Col>
                    <Button
                      icon={copied ? <CheckOutlined /> : <CopyOutlined />}
                      onClick={() => copyToClipboard(selectedCall.transcript)}
                      style={{
                        background: copied ? '#52c41a' : '#667eea',
                        border: 'none',
                      }}
                      type="primary"
                    >
                      {copied ? 'Copied!' : 'Copy'}
                    </Button>
                  </Col>
                </Row>
                <div
                  style={{
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-word',
                    marginTop: 16,
                  }}
                >
                  {(() => {
                    let personalization =
                      selectedCall.prompt_result?.personalization;
                    if (
                      !personalization ||
                      (!personalization.assistant_name &&
                        !personalization.agents_nickname)
                    ) {
                      personalization = defaultPersonalization;
                    }
                    const transcript = selectedCall.transcript;
                    let assistantName = 'Agent';
                    let agentNickname = 'User';
                    if (personalization) {
                      assistantName = personalization.assistant_name || 'Alex';
                      agentNickname =
                        personalization.agents_nickname || agent.first_name;
                    } else {
                      assistantName = 'Alex';
                      agentNickname = agent.first_name;
                    }
                    const coloredTranscript = colorizeTranscript(
                      transcript,
                      assistantName,
                      agentNickname,
                    );
                    return (
                      <div
                        dangerouslySetInnerHTML={{
                          __html: sanitizeHTML(coloredTranscript),
                        }}
                      />
                    );
                  })()}
                </div>
              </Card>
            )}
          </Space>
        )}
      </Modal>
    </Card>
  );
};

export default CallList;
